import React, { useCallback, useEffect, useState } from 'react';
import {
    createPlatformFeeTier,
    deletePlatformFeeTier,
    getPlatformFeeRevenue,
    getPlatformFeeTiers,
    previewPlatformFee,
    updatePlatformFeeTier,
} from '../../services/api.admin';
import type { PlatformFeeRevenueDto, PlatformFeeTierDto, UpsertPlatformFeeTierDto } from '../../types';

/** Empty max (∞) must stay null — never Number('') which is 0. */
function parseMaxPrice(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === '') return null;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(n)) return null;
    return n;
}

function buildNewTierForm(existing: PlatformFeeTierDto[]): UpsertPlatformFeeTierDto {
    const sortOrder =
        existing.length > 0 ? Math.max(...existing.map((x) => x.sortOrder)) + 1 : 1;
    // Suggest min after highest finite max so ranges don't immediately overlap
    const finiteMaxes = existing
        .map((t) => t.maxPrice)
        .filter((m): m is number => m != null && !Number.isNaN(Number(m)))
        .map(Number);
    const nextMin = finiteMaxes.length > 0 ? Math.max(...finiteMaxes) : 0;
    const hasOpenEndedActive = existing.some((t) => t.isActive && t.maxPrice == null);
    // New tiers start inactive when open-ended coverage exists — activate after adjusting range
    return {
        minPrice: nextMin,
        maxPrice: null,
        percentage: 5,
        sortOrder,
        isActive: !hasOpenEndedActive,
        label: '',
    };
}

/**
 * Admin-only platform fee bands + revenue.
 * Tiers are never loaded by the shop frontend.
 */
const PlatformFeeSection: React.FC = () => {
    const [tiers, setTiers] = useState<PlatformFeeTierDto[]>([]);
    const [revenue, setRevenue] = useState<PlatformFeeRevenueDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<UpsertPlatformFeeTierDto>(() => buildNewTierForm([]));
    const [previewPrice, setPreviewPrice] = useState(15);
    const [previewResult, setPreviewResult] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async (): Promise<PlatformFeeTierDto[]> => {
        setLoading(true);
        setError(null);
        try {
            const [t, r] = await Promise.all([getPlatformFeeTiers(), getPlatformFeeRevenue()]);
            const list = Array.isArray(t) ? t : (t as { $values?: PlatformFeeTierDto[] })?.$values ?? [];
            setTiers(list);
            setRevenue(r);
            return list;
        } catch {
            setError('Failed to load platform fee data.');
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load().then((list) => {
            setForm(buildNewTierForm(list));
        });
    }, [load]);

    const startCreate = () => {
        setEditingId(null);
        setError(null);
        setForm(buildNewTierForm(tiers));
    };

    const startEdit = (t: PlatformFeeTierDto) => {
        setEditingId(t.id);
        setForm({
            minPrice: t.minPrice,
            maxPrice: t.maxPrice,
            percentage: t.percentage,
            sortOrder: t.sortOrder,
            isActive: t.isActive,
            label: t.label ?? '',
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setError(null);
        setForm(buildNewTierForm(tiers));
    };

    const apiErrorMessage = (e: unknown): string => {
        const data = (e as { response?: { data?: { errors?: Record<string, string | string[]> | { General?: string } } } })
            ?.response?.data;
        const errors = data?.errors;
        if (!errors) return 'Save failed.';
        if (typeof (errors as { General?: string }).General === 'string') {
            return (errors as { General: string }).General;
        }
        // ModelState-style or multi-field
        const parts = Object.entries(errors as Record<string, string | string[]>).flatMap(([k, v]) => {
            const text = Array.isArray(v) ? v.join(' ') : String(v);
            return text ? [`${k}: ${text}`] : [];
        });
        return parts.length > 0 ? parts.join(' ') : 'Save failed.';
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const maxPrice = parseMaxPrice(form.maxPrice);
            const minPrice = Number(form.minPrice);
            if (Number.isNaN(minPrice) || minPrice < 0) {
                setError('Min price must be a number ≥ 0.');
                return;
            }
            if (maxPrice != null && maxPrice <= minPrice) {
                setError('Max price must be greater than min (or leave empty for ∞).');
                return;
            }
            const percentage = Number(form.percentage);
            if (Number.isNaN(percentage) || percentage < 0 || percentage > 100) {
                setError('Percentage must be between 0 and 100.');
                return;
            }

            const payload: UpsertPlatformFeeTierDto = {
                minPrice,
                maxPrice,
                percentage,
                sortOrder: Number(form.sortOrder) || 0,
                isActive: !!form.isActive,
                label: form.label?.trim() || null,
            };
            if (editingId != null) {
                await updatePlatformFeeTier(editingId, payload);
            } else {
                await createPlatformFeeTier(payload);
            }
            setEditingId(null);
            const list = await load();
            setForm(buildNewTierForm(list));
        } catch (e: unknown) {
            setError(apiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id: number) => {
        if (!window.confirm('Delete this fee tier?')) return;
        setError(null);
        try {
            await deletePlatformFeeTier(id);
            await load();
        } catch {
            setError('Delete failed.');
        }
    };

    const runPreview = async () => {
        try {
            const p = await previewPlatformFee(Number(previewPrice) || 0);
            setPreviewResult(
                p.applied
                    ? `${p.title}: €${p.feeAmount.toFixed(2)} (${p.percentageApplied}% of €${p.itemPrice.toFixed(2)})`
                    : 'No fee applied (disabled or no matching band).'
            );
        } catch {
            setPreviewResult('Preview failed.');
        }
    };

    if (loading) {
        return (
            <section className="mt-8 rounded-lg bg-white p-8 shadow-lg">
                <p className="text-gray-600">Loading platform fees…</p>
            </section>
        );
    }

    return (
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-2 text-xl font-bold text-gray-800">Platform fee bands (buyer protection)</h2>
            <p className="mb-4 text-sm text-gray-500">
                Charged to the buyer on top of list price at checkout. Sellers keep 100% of list. Bands are{' '}
                <strong>admin-only</strong> (not on public shop APIs). Enable / line title: section above — click{' '}
                <strong>Save Settings</strong> for toggles; tier edits save immediately.
            </p>

            {revenue && (
                <div className="mb-6 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
                    Platform fee revenue (paid-like orders):{' '}
                    <strong>
                        €{Number(revenue.totalPlatformFees).toFixed(2)} {revenue.currencyCode}
                    </strong>{' '}
                    across {revenue.orderCount} fee-bearing order(s).
                </div>
            )}

            {error && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="mb-6 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                    <thead>
                        <tr className="border-b text-gray-600">
                            <th className="py-2 pr-3">Order</th>
                            <th className="py-2 pr-3">Label</th>
                            <th className="py-2 pr-3">Min €</th>
                            <th className="py-2 pr-3">Max € (excl.)</th>
                            <th className="py-2 pr-3">%</th>
                            <th className="py-2 pr-3">Active</th>
                            <th className="py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tiers.map((t) => (
                            <tr key={t.id} className="border-b border-gray-100">
                                <td className="py-2 pr-3">{t.sortOrder}</td>
                                <td className="py-2 pr-3">{t.label || '—'}</td>
                                <td className="py-2 pr-3">{Number(t.minPrice).toFixed(2)}</td>
                                <td className="py-2 pr-3">
                                    {t.maxPrice == null ? '∞' : Number(t.maxPrice).toFixed(2)}
                                </td>
                                <td className="py-2 pr-3">{Number(t.percentage)}%</td>
                                <td className="py-2 pr-3">{t.isActive ? 'Yes' : 'No'}</td>
                                <td className="py-2 space-x-2">
                                    <button
                                        type="button"
                                        className="text-indigo-600 hover:underline"
                                        onClick={() => startEdit(t)}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        className="text-red-600 hover:underline"
                                        onClick={() => void remove(t.id)}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {tiers.length === 0 && (
                            <tr>
                                <td colSpan={7} className="py-4 text-gray-500">
                                    No tiers yet. Add bands covering your full price range.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-1 text-lg font-semibold text-gray-800">
                    {editingId != null ? `Edit tier #${editingId}` : 'Add tier'}
                </h3>
                <p className="mb-3 text-xs text-gray-500">
                    Bands are half-open: min inclusive, max exclusive. Empty max = ∞. Active ranges must not
                    overlap — if you already have an open-ended active band (e.g. 100+), new tiers start{' '}
                    <strong>inactive</strong> until you shrink/deactivate the open band, then activate.
                </p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">Min price (incl.)</label>
                        <input
                            type="number"
                            min={0}
                            step="0.01"
                            className="w-full rounded border px-3 py-2"
                            value={form.minPrice}
                            onChange={(e) => setForm((f) => ({ ...f, minPrice: Number(e.target.value) }))}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                            Max price (excl., empty = ∞)
                        </label>
                        <input
                            type="number"
                            min={0}
                            step="0.01"
                            className="w-full rounded border px-3 py-2"
                            value={form.maxPrice ?? ''}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    maxPrice: e.target.value === '' ? null : Number(e.target.value),
                                }))
                            }
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">Percentage</label>
                        <input
                            type="number"
                            min={0}
                            max={100}
                            step="0.1"
                            className="w-full rounded border px-3 py-2"
                            value={form.percentage}
                            onChange={(e) => setForm((f) => ({ ...f, percentage: Number(e.target.value) }))}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">Sort order</label>
                        <input
                            type="number"
                            className="w-full rounded border px-3 py-2"
                            value={form.sortOrder}
                            onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">Label</label>
                        <input
                            type="text"
                            className="w-full rounded border px-3 py-2"
                            value={form.label ?? ''}
                            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                        />
                    </div>
                    <div className="flex items-end gap-2 pb-2">
                        <input
                            id="tierActive"
                            type="checkbox"
                            checked={form.isActive}
                            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                            className="h-4 w-4"
                        />
                        <label htmlFor="tierActive" className="text-sm text-gray-700">
                            Active
                        </label>
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                    <button
                        type="button"
                        disabled={saving}
                        onClick={() => void save()}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                        {saving ? 'Saving…' : editingId != null ? 'Update tier' : 'Create tier'}
                    </button>
                    {editingId != null ? (
                        <button
                            type="button"
                            onClick={cancelEdit}
                            className="rounded-lg border px-4 py-2 text-sm text-gray-700"
                        >
                            Cancel edit
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={startCreate}
                            className="rounded-lg border px-4 py-2 text-sm text-gray-700"
                        >
                            Reset form
                        </button>
                    )}
                </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
                <h3 className="mb-2 text-sm font-semibold text-gray-800">Preview fee for list price</h3>
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="mb-1 block text-xs text-gray-600">Item price €</label>
                        <input
                            type="number"
                            min={0}
                            step="0.01"
                            className="w-32 rounded border px-3 py-2"
                            value={previewPrice}
                            onChange={(e) => setPreviewPrice(Number(e.target.value))}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => void runPreview()}
                        className="rounded-lg bg-gray-800 px-4 py-2 text-sm text-white"
                    >
                        Preview
                    </button>
                    {previewResult && <p className="text-sm text-gray-700">{previewResult}</p>}
                </div>
            </div>
        </section>
    );
};

export default PlatformFeeSection;
